/**
 * @fileoverview Reports how a full page load started, when that load looks
 * like something a person had to recover from: a reload, a page Chrome had
 * discarded, or the same page loading again soon after the last load in this
 * tab.
 *
 * Production forwards every `console.warn` to the `logWarning` callable (see
 * `productionLogging.ts`), so ordinary loads are deliberately not reported.
 *
 * Reloads our own code starts go through `reloadPage` in `appReload.ts`, so
 * they carry a reason. What a report cannot tell apart: a tab that crashed or
 * hung ("Aw, Snap!") and was then reloaded reads as an ordinary user `reload`
 * with no reason, because a crash leaves nothing the next load can observe. A
 * short {@link PageLoadReport.previousLoadAgoMs} on the same page is the
 * practical sign of a crash or reload loop.
 */

import {type AppReloadReason, takeAppReloadReason} from 'appReload';
import {BUILD_COMMIT} from 'utils/general';
import {z} from 'zod';

/** sessionStorage key holding the previous full load in this tab. */
const LAST_LOAD_KEY = 'cip_last_page_load';

/**
 * How soon a second full load of the same page counts as a repeat worth
 * reporting even when the browser calls it a fresh navigation. A live section
 * is an hour long and the reloads that prompted this report came a few minutes
 * apart, while a person returning to a page later in the day should not count.
 */
export const REPEAT_LOAD_WINDOW_MS = 10 * 60 * 1000;

/** The previous full load in this tab, as stored between loads. */
const PreviousLoadSchema = z.object({
  pathname: z.string(),
  loadedAt: z.number(),
  build: z.string(),
});

/** The previous full load in this tab. */
export type PreviousLoad = z.infer<typeof PreviousLoadSchema>;

/** What a page-load report carries to Cloud Logging. */
export interface PageLoadReport {
  /**
   * The browser's navigation type for this document (`navigate`, `reload`,
   * `back_forward`, ...), or `'unknown'` where Navigation Timing is missing.
   * Chrome restores a discarded tab as `back_forward`, not `reload` (from
   * Chromium source, not documented), so read it with
   * {@link PageLoadReport.wasDiscarded}.
   */
  navigationType: string;
  /**
   * True when Chrome discarded this page in a hidden tab and the load is its
   * restore; null in browsers without `document.wasDiscarded`. Chrome sets it
   * on the restored document only, so a later reload reads false.
   */
  wasDiscarded: boolean | null;
  /**
   * Milliseconds since the previous full load in this tab; null if none. After
   * a discard it approximates how long the tab sat before it was discarded and
   * restored.
   */
  previousLoadAgoMs: number | null;
  /** True when the previous full load in this tab was of the same page. */
  samePathAsPreviousLoad: boolean;
  /** The build the previous full load in this tab ran; null if none. */
  previousBuild: string | null;
  /** The page: its pathname, plus the `slide` query value when present. */
  path: string;
  /** The build this load runs. */
  build: string;
  /**
   * True when another window opened this one, such as the windowed present
   * popup. Such a window inherits the opener's sessionStorage, so its first
   * load is not counted as a repeat.
   */
  hasOpener: boolean;
  /** Why our own code reloaded the page; null when it did not. */
  appReloadReason: AppReloadReason | null;
}

/**
 * Reads the navigation type of the current document.
 *
 * @returns The Navigation Timing `type`, or `'unknown'` where the browser has
 *   no navigation entry.
 * @throws Never.
 */
export function readNavigationType(): string {
  if (
    typeof PerformanceNavigationTiming === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return 'unknown';
  }
  const [entry] = performance.getEntriesByType('navigation');
  if (!(entry instanceof PerformanceNavigationTiming)) {
    return 'unknown';
  }
  return entry.type;
}

/**
 * Reads whether Chrome restored this page after discarding it.
 *
 * @returns `document.wasDiscarded`, or null in browsers without it.
 * @throws Never.
 */
export function readWasDiscarded(): boolean | null {
  if (!('wasDiscarded' in document)) {
    return null;
  }
  return Boolean(document.wasDiscarded);
}

/**
 * Reads whether another window opened this one.
 *
 * @returns True when `window.opener` is set.
 * @throws Never.
 */
export function readHasOpener(): boolean {
  return window.opener !== null;
}

/**
 * Names the page for a report without carrying query values that could
 * identify someone: the pathname, plus the `slide` value when present.
 *
 * @param location - The location to describe.
 * @returns The pathname, with `?slide=<n>` appended when the URL has one.
 * @throws Never.
 */
export function describePagePath(location: {
  pathname: string;
  search: string;
}): string {
  const slide = new URLSearchParams(location.search).get('slide');
  return slide === null
    ? location.pathname
    : `${location.pathname}?slide=${encodeURIComponent(slide)}`;
}

/**
 * Decides whether a full page load is worth reporting, and what to report.
 *
 * A load is reported when the browser calls it a reload, when Chrome restored
 * it after a discard, or when the previous load in this tab was the same
 * pathname within {@link REPEAT_LOAD_WINDOW_MS}. The repeat window is the
 * closed interval `[0, REPEAT_LOAD_WINDOW_MS]` since the previous load. A
 * window's first navigation with an opener is never a repeat: the previous
 * load it sees is the opener's.
 *
 * @param params.navigationType - From {@link readNavigationType}.
 * @param params.wasDiscarded - From {@link readWasDiscarded}.
 * @param params.previousLoad - The previous full load in this tab, or
 *   undefined when this is the tab's first.
 * @param params.pathname - The current pathname, compared with the previous
 *   load's.
 * @param params.path - The page as reported; see {@link describePagePath}.
 * @param params.build - The build this load runs.
 * @param params.hasOpener - From {@link readHasOpener}.
 * @param params.appReloadReason - From `takeAppReloadReason`.
 * @param params.now - The time of this load.
 * @returns The report, or null when the load is ordinary.
 * @throws Never.
 */
export function describePageLoad({
  navigationType,
  wasDiscarded,
  previousLoad,
  pathname,
  path,
  build,
  hasOpener,
  appReloadReason,
  now,
}: {
  navigationType: string;
  wasDiscarded: boolean | null;
  previousLoad: PreviousLoad | undefined;
  pathname: string;
  path: string;
  build: string;
  hasOpener: boolean;
  appReloadReason: AppReloadReason | null;
  now: Date;
}): PageLoadReport | null {
  const previousLoadAgoMs = previousLoad
    ? now.getTime() - previousLoad.loadedAt
    : null;
  const samePathAsPreviousLoad = previousLoad?.pathname === pathname;
  const isOpenedWindowsFirstLoad = hasOpener && navigationType === 'navigate';
  const isRepeat =
    !isOpenedWindowsFirstLoad &&
    samePathAsPreviousLoad &&
    previousLoadAgoMs !== null &&
    previousLoadAgoMs >= 0 &&
    previousLoadAgoMs <= REPEAT_LOAD_WINDOW_MS;
  if (navigationType !== 'reload' && wasDiscarded !== true && !isRepeat) {
    return null;
  }
  return {
    navigationType,
    wasDiscarded,
    previousLoadAgoMs,
    samePathAsPreviousLoad,
    previousBuild: previousLoad?.build ?? null,
    path,
    build,
    hasOpener,
    appReloadReason,
  };
}

function readPreviousLoad(): PreviousLoad | undefined {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(LAST_LOAD_KEY);
  } catch (e) {
    console.debug('[page-load] reading the previous load failed', e);
    return undefined;
  }
  if (stored === null) {
    return undefined;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch (e) {
    console.debug('[page-load] the previous load is not JSON', e);
    return undefined;
  }
  const result = PreviousLoadSchema.safeParse(parsed);
  return result.success ? result.data : undefined;
}

function recordLoad(load: PreviousLoad): void {
  try {
    sessionStorage.setItem(LAST_LOAD_KEY, JSON.stringify(load));
  } catch (e) {
    console.debug('[page-load] recording this load failed', e);
  }
}

/**
 * Records this full page load for the next one to compare against, and logs
 * one `[page-load]` warning when {@link describePageLoad} finds it worth
 * reporting.
 *
 * Call once per document, after production logging is installed. The state is
 * read and recorded synchronously, so a quick second load still sees this one;
 * only the warning waits for `whenReadyToLog`. The app reload marker is taken
 * on every load, reported or not, so it never labels a later one.
 *
 * @param params.whenReadyToLog - Settles once the warning can be attributed,
 *   e.g. when auth has restored the user. The warning is sent whether it
 *   resolves or rejects. Omitted → sent immediately.
 * @param params.now - The time of this load, for tests. Omitted → the current
 *   time.
 * @returns Nothing.
 * @throws Never — storage failures are logged at debug level.
 */
export function reportPageLoad({
  whenReadyToLog,
  now = new Date(),
}: {whenReadyToLog?: Promise<unknown>; now?: Date} = {}): void {
  const appReloadReason = takeAppReloadReason({now});
  const previousLoad = readPreviousLoad();
  const {pathname} = window.location;
  recordLoad({pathname, loadedAt: now.getTime(), build: BUILD_COMMIT});
  const report = describePageLoad({
    navigationType: readNavigationType(),
    wasDiscarded: readWasDiscarded(),
    previousLoad,
    pathname,
    path: describePagePath(window.location),
    build: BUILD_COMMIT,
    hasOpener: readHasOpener(),
    appReloadReason,
    now,
  });
  if (!report) {
    return;
  }
  const warn = () => {
    console.warn('[page-load] a page loaded again', report);
  };
  if (!whenReadyToLog) {
    warn();
    return;
  }
  void whenReadyToLog.then(warn, warn);
}
